import {WeightGrid} from './basinWeights';

/**
 * A slice of gridded values as returned by zarrita: flat data plus shape/stride.
 * Shape is (ensemble_member, lead_time, y, x) for ensemble datasets and
 * (lead_time, y, x) otherwise.
 */
export type GriddedSlice = {
  data: Float32Array | Float64Array;
  shape: number[];
  stride: number[];
};

/**
 * Basin-average each (member, lead) grid then average across members, both as
 * NaN-skipping weighted/unweighted means — the same reduction xarray performs in
 * the backend (`.weighted(w).mean(dim=[y, x])` followed by `.mean("ensemble_member")`).
 * Returns one value per lead time; leads with no finite cells are NaN.
 */
export function basinAverageSeries(slice: GriddedSlice, weightGrid: WeightGrid): Float64Array {
  const hasEnsemble = slice.shape.length === 4;
  const [members, leads, rows, cols] = hasEnsemble
    ? slice.shape
    : [1, slice.shape[0], slice.shape[1], slice.shape[2]];
  const [memberStride, leadStride, rowStride, colStride] = hasEnsemble
    ? slice.stride
    : [0, slice.stride[0], slice.stride[1], slice.stride[2]];

  if (rows !== weightGrid.rows || cols !== weightGrid.cols) {
    throw new Error(
      `Data window (${rows}×${cols}) does not match weight grid (${weightGrid.rows}×${weightGrid.cols})`
    );
  }

  const {weights} = weightGrid;
  const result = new Float64Array(leads);
  for (let lead = 0; lead < leads; lead += 1) {
    let memberSum = 0;
    let memberCount = 0;
    for (let member = 0; member < members; member += 1) {
      const base = member * memberStride + lead * leadStride;
      let weightedSum = 0;
      let weightSum = 0;
      for (let row = 0; row < rows; row += 1) {
        const rowBase = base + row * rowStride;
        const weightRowBase = row * cols;
        for (let col = 0; col < cols; col += 1) {
          const weight = weights[weightRowBase + col];
          if (weight === 0) {
            continue;
          }
          const value = slice.data[rowBase + col * colStride];
          if (!Number.isNaN(value)) {
            weightedSum += weight * value;
            weightSum += weight;
          }
        }
      }
      if (weightSum > 0) {
        memberSum += weightedSum / weightSum;
        memberCount += 1;
      }
    }
    result[lead] = memberCount > 0 ? memberSum / memberCount : NaN;
  }
  return result;
}
